// Omnibox for adding trip content: free-text custom ideas (category picker,
// duration) or any pasted URL, which is sent to /api/tp/link/extract for
// AI extraction into a structured idea. Rendered on Brainstorm and Itinerary
// screens; onAdd prepends so new ideas appear near the top.
import React from 'react';
import { PALETTE } from '../theme';
import { Icon, Chip } from './UI';
import { useAuth } from '../contexts/AuthContext';
import { isSafeHttpUrl } from '../utils/url';

export const CUSTOM_CATS_ALL = ['Sights', 'Food', 'Experience', 'Day trip', 'Shopping', 'Nightlife', 'Hotel', 'Custom'];

const SOCIAL_RE = /instagram\.com|tiktok\.com/i;
const ANY_URL_RE = /^https?:\/\//i;

const labelStyle = {
  fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1,
  textTransform: 'uppercase', color: PALETTE.inkMute, minWidth: 80,
};
const inputStyle = {
  width: '100%', boxSizing: 'border-box', appearance: 'none',
  border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '7px 12px',
  fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, background: PALETTE.paper, outline: 'none',
};

// Multi-city trips: custom ideas have no natural city, so anywhere one gets
// created (typed, pasted link, or extracted from a social post) needs a way
// to say which leg it belongs to. cityIdx starts at null (untagged) — an
// idea only gets a legIndex/destCity if the user explicitly taps a city
// chip; it never silently defaults to leg 0. Untagged ideas show up under
// an "Untagged" group instead of being guessed into the wrong city.
function cityNameForLeg(legs, i) {
  return (legs?.[i]?.destination || '').split(',')[0].trim();
}

function CityPicker({ legs, cityIdx, setCityIdx, label = 'Add to', allowUntag = false }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
      <span style={{ ...labelStyle, minWidth: 0 }}>{label}</span>
      {allowUntag && (
        <Chip active={cityIdx == null} onClick={() => setCityIdx(null)} style={{ padding: '3px 10px', fontSize: 11 }}>
          Untagged
        </Chip>
      )}
      {legs.map((l, i) => (
        <Chip key={i} active={cityIdx === i} onClick={() => setCityIdx(i)} style={{ padding: '3px 10px', fontSize: 11 }}>
          {cityNameForLeg(legs, i)}
        </Chip>
      ))}
    </div>
  );
}

function LinkEditForm({ item, sourceUrl, accent, onAdd, onCancel, legs = null, initialCityIdx = null }) {
  const [title, setTitle] = React.useState(item.title || '');
  const [cat, setCat] = React.useState(item.category || 'Experience');
  const [dur, setDur] = React.useState(1);
  const [area, setArea] = React.useState(item.destination || '');
  const [notes, setNotes] = React.useState(item.notes || '');
  const [requiresBooking, setRequiresBooking] = React.useState(!!item.requiresBooking);
  const isMultiCity = legs && legs.length > 1;
  const [cityIdx, setCityIdx] = React.useState(initialCityIdx);

  const submit = () => {
    if (!title.trim()) return;
    onAdd({
      id: `link_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, title: title.trim(), cat, dur, area, meta: notes, requiresBooking, sourceUrl,
      ...(isMultiCity && cityIdx != null ? { legIndex: cityIdx, destCity: cityNameForLeg(legs, cityIdx) } : {}),
    });
  };

  return (
    <div style={{ marginTop: 8, padding: 14, background: PALETTE.card, borderRadius: 12, border: `0.5px solid ${PALETTE.ruleSoft}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.inkMute }}>
        From link — edit before adding
      </div>
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title" style={inputStyle} autoFocus />
      {isMultiCity && <CityPicker legs={legs} cityIdx={cityIdx} setCityIdx={setCityIdx} allowUntag />}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {CUSTOM_CATS_ALL.map(c => (
          <button key={c} onClick={() => setCat(c)} style={{
            appearance: 'none', cursor: 'pointer', padding: '4px 10px', borderRadius: 999,
            background: cat === c ? PALETTE.ink : 'transparent',
            color: cat === c ? PALETTE.paper : PALETTE.inkSoft,
            fontFamily: 'Inter', fontSize: 11, fontWeight: 500,
            border: cat === c ? 'none' : `1px solid ${PALETTE.rule}`,
          }}>{c}</button>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={labelStyle}>Duration</span>
        <button onClick={() => setDur(d => Math.max(0.5, +(d - 0.5).toFixed(1)))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 6, width: 26, height: 26, background: PALETTE.paper, cursor: 'pointer', color: PALETTE.ink, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
        <span style={{ fontFamily: 'Fraunces', fontSize: 15, color: PALETTE.ink, minWidth: 32, textAlign: 'center' }}>{dur < 1 ? `${dur * 60}m` : `${dur}h`}</span>
        <button onClick={() => setDur(d => Math.min(12, +(d + 0.5).toFixed(1)))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 6, width: 26, height: 26, background: PALETTE.paper, cursor: 'pointer', color: PALETTE.ink, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
      </div>
      <input value={area} onChange={e => setArea(e.target.value)} placeholder="Location / area (optional)" style={inputStyle} />
      <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" style={inputStyle} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={labelStyle}>Require booking</span>
        <button onClick={() => setRequiresBooking(r => !r)} style={{
          appearance: 'none', cursor: 'pointer', padding: '4px 12px', borderRadius: 999,
          background: requiresBooking ? accent : 'transparent',
          color: requiresBooking ? '#fff' : PALETTE.inkSoft,
          fontFamily: 'Inter', fontSize: 11, fontWeight: 500,
          border: requiresBooking ? 'none' : `1px solid ${PALETTE.rule}`,
        }}>{requiresBooking ? 'Yes — needs booking' : 'No'}</button>
      </div>
      {isSafeHttpUrl(sourceUrl) && (
        <a href={sourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.3, wordBreak: 'break-all', textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
          <span style={{ flexShrink: 0 }}>🔗</span><span style={{ opacity: 0.7 }}>{sourceUrl}</span>
        </a>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={submit} style={{ flex: 1, appearance: 'none', border: 0, background: accent, color: '#fff', borderRadius: 8, padding: '8px 0', fontFamily: 'Inter', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add to trip</button>
        <button onClick={onCancel} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: 'transparent', color: PALETTE.ink, borderRadius: 8, padding: '8px 14px', fontFamily: 'Inter', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
      </div>
    </div>
  );
}

export function IdeaBar({ onAdd, accent, legs = null, onQueryChange = null, defaultCityIdx = null }) {
  const { user } = useAuth();
  const [value, setValue] = React.useState('');
  const [expanded, setExpanded] = React.useState(false);
  const [cat, setCat] = React.useState('Custom');
  const [dur, setDur] = React.useState(1);
  const [area, setArea] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [requiresBooking, setRequiresBooking] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [extractedItems, setExtractedItems] = React.useState([]);
  const [extractedSourceUrl, setExtractedSourceUrl] = React.useState('');
  const [linkItem, setLinkItem] = React.useState(null);
  const [linkSourceUrl, setLinkSourceUrl] = React.useState('');
  const [extractError, setExtractError] = React.useState('');
  const isMultiCity = legs && legs.length > 1;
  const [cityIdx, setCityIdx] = React.useState(defaultCityIdx);
  // Tracks whichever city tab is active in the Ideas tab above this bar, so
  // a typed idea auto-tags to that city without an extra tap — switching
  // tabs re-presets the tag; a manual pick in between only sticks until the
  // next tab switch.
  React.useEffect(() => { setCityIdx(defaultCityIdx); }, [defaultCityIdx]);
  const cityTag = isMultiCity && cityIdx != null ? { legIndex: cityIdx, destCity: cityNameForLeg(legs, cityIdx) } : {};

  const isUrl = (s) => ANY_URL_RE.test(s.trim());

  const handleExtract = React.useCallback(async (url) => {
    setLoading(true); setExtractError(''); setExtractedItems([]); setLinkItem(null);
    try {
      const token = user ? await user.getIdToken() : '';
      const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      if (SOCIAL_RE.test(url)) {
        const res = await fetch('/api/tp/ig/extract', {
          method: 'POST', headers: authHeaders,
          body: JSON.stringify({ url: url.trim() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not extract');
        setExtractedItems(data.items || []);
        setExtractedSourceUrl(data.sourceUrl || url.trim());
      } else {
        const res = await fetch('/api/tp/link/extract', {
          method: 'POST', headers: authHeaders,
          body: JSON.stringify({ url: url.trim() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not extract');
        setLinkItem(data.item || {});
        setLinkSourceUrl(data.sourceUrl || url.trim());
      }
    } catch (err) { setExtractError(err.message); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  const handleSubmit = () => {
    const v = value.trim();
    if (!v) return;
    if (isUrl(v)) {
      handleExtract(v);
    } else {
      onAdd?.({ id: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, title: v, cat, dur, area, meta: notes, requiresBooking, ...cityTag });
      setValue(''); setExpanded(false); setArea(''); setNotes(''); setRequiresBooking(false);
      onQueryChange?.('');
    }
  };

  const handleAddExtracted = (item, idx) => {
    onAdd?.({ id: `ig_${Date.now()}_${idx}`, title: item.title, cat: item.category || 'Experience', dur: 2, area: item.destination || '', meta: item.notes || '', requiresBooking: false, sourceUrl: extractedSourceUrl, ...cityTag });
    setExtractedItems(prev => prev.filter((_, j) => j !== idx));
    if (extractedItems.length <= 1) { setValue(''); setExtractedSourceUrl(''); onQueryChange?.(''); }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);
    onQueryChange?.(v);
    if (extractedItems.length > 0) { setExtractedItems([]); setExtractedSourceUrl(''); }
    if (linkItem) setLinkItem(null);
    if (extractError) setExtractError('');
  };

  // Gated behind `expanded` (the ▾ "add details" toggle) — the active city
  // tab already auto-tags typed ideas silently (see defaultCityIdx above),
  // so popping this chip row open on every keystroke was noise; it's only
  // needed when someone wants to override the auto-tag by hand.
  const showCityPicker = isMultiCity && expanded && ((value.trim() && !isUrl(value)) || extractedItems.length > 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: PALETTE.card, borderRadius: 12, border: `1px solid ${PALETTE.rule}` }}>
        <Icon name={value.trim() ? 'plus' : 'search'} size={15} style={{ color: PALETTE.inkMute, flexShrink: 0 }} />
        <input
          value={value}
          onChange={handleChange}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSubmit();
            if (e.key === 'Escape') { setValue(''); setExpanded(false); setExtractedItems([]); setLinkItem(null); onQueryChange?.(''); }
          }}
          placeholder="Search your ideas, or add a new one, hotel, or link…"
          style={{ flex: 1, appearance: 'none', border: 0, outline: 'none', background: 'transparent', fontFamily: 'Inter', fontSize: 14, color: PALETTE.ink }}
        />
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {value.trim() && (
            <>
              <button onClick={() => { setValue(''); onQueryChange?.(''); }} aria-label="Clear" style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: '4px 2px', lineHeight: 1 }}>✕</button>
              <button onClick={handleSubmit} disabled={loading} style={{ appearance: 'none', border: 0, background: accent, color: '#fff', borderRadius: 8, padding: '4px 10px', fontFamily: 'Inter', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                {loading ? '…' : isUrl(value) ? 'Extract' : 'Add'}
              </button>
            </>
          )}
          <button onClick={() => setExpanded(e => !e)} aria-label={expanded ? 'Collapse idea options' : 'Expand idea options'} aria-expanded={expanded} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, cursor: 'pointer', padding: 5, margin: '-5px -3px -5px 0', borderRadius: 6, display: 'flex', alignItems: 'center' }}>
            <Icon name={expanded ? 'arrowUp' : 'arrowDn'} size={13} />
          </button>
        </div>
      </div>

      {showCityPicker && (
        <div style={{ marginTop: 8 }}>
          <CityPicker legs={legs} cityIdx={cityIdx} setCityIdx={setCityIdx} allowUntag />
        </div>
      )}

      {extractError && (
        <div style={{ marginTop: 6, padding: '8px 12px', background: PALETTE.redSoft, borderRadius: 8, fontFamily: 'Inter', fontSize: 12, color: PALETTE.red }}>{extractError}</div>
      )}

      {/* Social (Instagram/TikTok): tap-to-add list — source link shown at top */}
      {extractedItems.length > 0 && (
        <div style={{ marginTop: 8, background: PALETTE.card, borderRadius: 10, border: `1px solid ${PALETTE.ruleSoft}`, overflow: 'hidden' }}>
          <div style={{ padding: '8px 14px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.inkMute }}>
              Found {extractedItems.length} idea{extractedItems.length !== 1 ? 's' : ''} — tap to add
            </div>
            {isSafeHttpUrl(extractedSourceUrl) && (
              <a href={extractedSourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.accent, textDecoration: 'none', flexShrink: 0 }}>🔗 source</a>
            )}
          </div>
          {extractedItems.map((item, i) => (
            <button key={i} onClick={() => handleAddExtracted(item, i)} style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
              padding: '9px 14px', appearance: 'none', border: 0, cursor: 'pointer',
              borderTop: `0.5px solid ${PALETTE.ruleSoft}`, background: 'transparent',
            }}
            onMouseEnter={e => e.currentTarget.style.background = PALETTE.paperDim}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Icon name="plus" size={14} style={{ color: accent, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Fraunces', fontSize: 14, color: PALETTE.ink }}>{item.title}</div>
                {item.destination && <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute }}>{item.destination}</div>}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Generic URL: editable pre-fill form */}
      {linkItem && (
        <LinkEditForm
          item={linkItem}
          sourceUrl={linkSourceUrl}
          accent={accent}
          legs={legs}
          initialCityIdx={cityIdx}
          onAdd={(idea) => { onAdd?.(idea); setLinkItem(null); setValue(''); onQueryChange?.(''); }}
          onCancel={() => { setLinkItem(null); setValue(''); onQueryChange?.(''); }}
        />
      )}

      {/* Manual expanded details */}
      {expanded && !isUrl(value) && (
        <div style={{ marginTop: 8, padding: 14, background: PALETTE.card, borderRadius: 12, border: `0.5px solid ${PALETTE.ruleSoft}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {CUSTOM_CATS_ALL.map(c => (
              <button key={c} onClick={() => setCat(c)} style={{
                appearance: 'none', cursor: 'pointer', padding: '4px 10px', borderRadius: 999,
                background: cat === c ? PALETTE.ink : 'transparent',
                color: cat === c ? PALETTE.paper : PALETTE.inkSoft,
                fontFamily: 'Inter', fontSize: 11, fontWeight: 500,
                border: cat === c ? 'none' : `1px solid ${PALETTE.rule}`,
              }}>{c}</button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={labelStyle}>Duration</span>
            <button onClick={() => setDur(d => Math.max(0.5, +(d - 0.5).toFixed(1)))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 6, width: 26, height: 26, background: PALETTE.paper, cursor: 'pointer', color: PALETTE.ink, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
            <span style={{ fontFamily: 'Fraunces', fontSize: 15, color: PALETTE.ink, minWidth: 32, textAlign: 'center' }}>{dur < 1 ? `${dur * 60}m` : `${dur}h`}</span>
            <button onClick={() => setDur(d => Math.min(12, +(d + 0.5).toFixed(1)))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 6, width: 26, height: 26, background: PALETTE.paper, cursor: 'pointer', color: PALETTE.ink, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
          </div>
          <input value={area} onChange={e => setArea(e.target.value)} placeholder="Location / area (optional)" style={inputStyle} />
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" style={inputStyle} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={labelStyle}>Require booking</span>
            <button onClick={() => setRequiresBooking(r => !r)} style={{
              appearance: 'none', cursor: 'pointer', padding: '4px 12px', borderRadius: 999,
              background: requiresBooking ? accent : 'transparent',
              color: requiresBooking ? '#fff' : PALETTE.inkSoft,
              fontFamily: 'Inter', fontSize: 11, fontWeight: 500,
              border: requiresBooking ? 'none' : `1px solid ${PALETTE.rule}`,
            }}>{requiresBooking ? 'Yes — needs booking' : 'No'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
